import { useCallback, useMemo, useRef } from 'react';

import { useAuth } from '@/lib/hooks/useAuth';
import api from '@/services';
import { useTokenStore, useUserStore } from '@/store';
import { APP_CONFIG_CONSTANTS } from '../../constants/appConfig.constants';
import { useDeviceFingerprint } from './useDeviceFingerprint';

export const useTokenManager = (apiKey: string) => {
  const { signOut, isAuthenticated } = useAuth();
  const { token, setToken, setTrialToken, trialToken } = useTokenStore();
  const { setUser, setAuthenticated } = useUserStore();
  const { getFingerprint } = useDeviceFingerprint();

  const isRefreshingToken = useRef(false);
  const isGettingTrialToken = useRef(false);

  const refreshToken = useCallback(async () => {
    if (!isAuthenticated || isRefreshingToken.current) return;

    isRefreshingToken.current = true;
    try {
      const res = await api.get('/auth/refresh-token', {
        timeout: APP_CONFIG_CONSTANTS.REQUEST_TIMEOUT,
      });

      const { token: newToken, ...userInfo } = res?.data?.data ?? {};
      if (newToken) {
        setToken(newToken);
        setUser(userInfo);
        setAuthenticated(true);
      } else {
        throw new Error('No token received from refresh endpoint');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      signOut({ apiCall: false });
      throw error;
    } finally {
      isRefreshingToken.current = false;
    }
  }, [isAuthenticated, setToken, setUser, setAuthenticated, signOut]);

  const getTrialToken = useCallback(async () => {
    if (isGettingTrialToken.current && isAuthenticated) {
      return;
    }

    isGettingTrialToken.current = true;
    try {
      const fingerprint = await getFingerprint();
      const res = await api.get('/user/trial-token', {
        headers: {
          'x-api-key': apiKey,
          'x-device-fingerprint': fingerprint,
        },
        timeout: APP_CONFIG_CONSTANTS.REQUEST_TIMEOUT,
      });

      const newTrialToken = res?.data?.data?.token;
      if (newTrialToken) {
        setTrialToken(newTrialToken);
      } else {
        throw new Error('No trial token received from server');
      }
    } catch (error) {
      console.error('Error getting trial token:', error);
      throw error;
    } finally {
      isGettingTrialToken.current = false;
    }
  }, [isAuthenticated, getFingerprint, apiKey, setTrialToken]);

  const isFetchingToken = useMemo(() => {
    return isRefreshingToken.current || isGettingTrialToken.current;
  }, [isRefreshingToken, isGettingTrialToken]);

  return {
    refreshToken,
    getTrialToken,
    token,
    trialToken,
    isAuthenticated,
    isFetchingToken,
  };
};
